import { Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { SessionDetail } from '../../models/session-detail';
import { BehaviorSubject, finalize, takeUntil } from 'rxjs';
import { AuthService } from '../../../auth/services/auth.service';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';

interface GroupedSessions {
  deviceType: string;
  deviceIcon: string;
  sessions: SessionDetail[];
  count: number;
}

@Component({
  selector: 'app-session-details',
  standalone: true,
  imports: [CommonModule, PopupComponent],
  templateUrl: './session-details.component.html',
  styleUrl: './session-details.component.scss',
})
export class SessionDetailsComponent extends DestroyBase implements OnInit {
  @Input() userId?: number; // user id is required only for admin to fetch sessions of other users, it will be empty for current user sessions

  authService = inject(AuthService);
  auditLogService = inject(AuditLogService);
  sessions$ = new BehaviorSubject<SessionDetail[]>([]);
  groupedSessions$ = new BehaviorSubject<GroupedSessions[]>([]);
  loading = true;
  error = '';
  showRevokeConfirmDialog = false;
  expandedDeviceGroups = new Set<string>();

  router = inject(Router);

  ngOnInit(): void {
    this.loadSessions();
  }

  loadSessions(): void {
    this.loading = true;
    this.authService
      .getUserSessions(this.userId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (sessions) => {
          this.sessions$.next(sessions);
          this.groupSessionsByDevice(sessions);
        },
        error: () => (this.error = 'Failed to load sessions'),
      });
  }

  private groupSessionsByDevice(sessions: SessionDetail[]): void {
    const groups = new Map<string, SessionDetail[]>();

    sessions.forEach((session) => {
      const deviceType = this.getDeviceType(session);
      if (!groups.has(deviceType)) {
        groups.set(deviceType, []);
      }
      groups.get(deviceType)?.push(session);
    });

    const groupedSessions: GroupedSessions[] = Array.from(groups.entries()).map(
      ([deviceType, sessions]) => ({
        deviceType,
        deviceIcon: this.getDeviceIcon(deviceType),
        sessions,
        count: sessions.length,
      }),
    );

    this.groupedSessions$.next(groupedSessions);
  }

  private getDeviceType(session: SessionDetail): string {
    const userAgent = session.user_agent?.toLowerCase() || '';
    // Remove extra quotes from device_info if present
    let deviceInfo = session.device_info || '';
    try {
      // Parse if it's JSON stringified
      deviceInfo = JSON.parse(deviceInfo);
    } catch {
      // If parsing fails, use as is
    }
    deviceInfo = deviceInfo.toLowerCase();

    if (userAgent.includes('iphone') || deviceInfo.includes('iphone')) {
      return 'iPhone';
    } else if (userAgent.includes('ipad') || deviceInfo.includes('ipad')) {
      return 'iPad';
    } else if (
      userAgent.includes('android') ||
      deviceInfo.includes('android')
    ) {
      return 'Android';
    } else if (userAgent.includes('mac') || deviceInfo.includes('mac')) {
      return 'Mac';
    } else if (
      userAgent.includes('windows') ||
      deviceInfo.includes('windows')
    ) {
      return 'Windows';
    } else if (userAgent.includes('linux') || deviceInfo.includes('linux')) {
      return 'Linux';
    }
    return 'Other';
  }

  private getDeviceIcon(deviceType: string): string {
    const icons: Record<string, string> = {
      iPhone: '📱',
      iPad: '📱',
      Android: '📱',
      Windows: '💻',
      Mac: '💻',
      Linux: '💻',
      Other: '🖥️',
    };
    return icons[deviceType] || '🖥️';
  }

  getBrowserName(userAgent: string): string {
    const ua = userAgent.toLowerCase();
    if (ua.includes('edg')) return 'Edge';
    if (ua.includes('chrome')) return 'Chrome';
    if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari';
    if (ua.includes('firefox')) return 'Firefox';
    if (ua.includes('opera')) return 'Opera';
    return 'Browser';
  }

  formatIpAddress(ip: string): string {
    if (ip === '127.0.0.1' || ip === '::1' || ip === 'localhost') {
      return 'Local';
    }
    return ip;
  }

  getCleanDeviceInfo(session: SessionDetail, fallback: string): string {
    if (!session.device_info) {
      return fallback;
    }
    let deviceInfo = session.device_info;
    try {
      // Parse if it's JSON stringified
      deviceInfo = JSON.parse(deviceInfo);
    } catch {
      // If parsing fails, use as is
    }
    return deviceInfo || fallback;
  }

  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60)
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  }

  onRevokeAllSessions(event: Event): void {
    event.stopPropagation();
    this.showRevokeConfirmDialog = true;
  }

  onCancelRevoke(): void {
    this.showRevokeConfirmDialog = false;
  }

  onConfirmRevoke(): void {
    this.showRevokeConfirmDialog = false;

    this.auditLogService.logFeature({
      featureName: 'revoke_sessions',
      action: 'revoke',
      meta: {
        userId: this.userId ? String(this.userId) : 'current_user',
        isAdminAction: this.userId ? 'true' : 'false',
      },
      description: 'User revokes all sessions',
    });

    this.authService
      .revokeAllSessions(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (!this.userId) {
            this.authService.clearLocalStore();
            this.router.navigate(['/login']);
          } else {
            this.sessions$.next([]);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 403) {
            alert(
              'You do not have permissions to revoke sessions for this user.',
            );
          } else {
            alert('Failed to revoke sessions. Please try again.');
          }
        },
      });
  }

  toggleDeviceGroup(deviceType: string): void {
    if (this.expandedDeviceGroups.has(deviceType)) {
      this.expandedDeviceGroups.delete(deviceType);
    } else {
      this.expandedDeviceGroups.add(deviceType);
    }
  }

  isDeviceGroupExpanded(deviceType: string): boolean {
    return this.expandedDeviceGroups.has(deviceType);
  }
}
